/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { generateScaleAnimationSequence, getStepDelay, AnimationStep } from '../utils/scaleAnimationUtils';

interface UseScaleAnimationProps {
  noteCollection: any;
  strings: number;
  fretRange: { start: number; end: number };
  tuning: string[];
  isEnabled: boolean;
}

interface AnimatedNote {
  stringIndex: number;
  fret: number;
  note: string;
  interval: string;
}

export function useScaleAnimation({
  noteCollection,
  strings,
  fretRange,
  tuning,
  isEnabled
}: UseScaleAnimationProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentAnimatedNote, setCurrentAnimatedNote] = useState<AnimatedNote | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentStepRef = useRef<number>(0);
  const sequenceRef = useRef<AnimationStep[]>([]);

  // Generate animation sequence when dependencies change
  useEffect(() => {
    if (isEnabled && noteCollection && tuning && tuning.length > 0) {
      sequenceRef.current = generateScaleAnimationSequence(
        noteCollection,
        strings,
        fretRange,
        tuning
      );
      // console.log('Scale animation sequence generated:', sequenceRef.current.length, 'steps');
      // if (sequenceRef.current.length > 0) {
      //   console.log('First few steps:', sequenceRef.current.slice(0, 5));
      // }
    } else {
      sequenceRef.current = [];
      // console.log('Scale animation disabled or no noteCollection/tuning');
    }
  }, [noteCollection, strings, fretRange, tuning, isEnabled]);

  // Clean up animation on unmount or when disabled
  useEffect(() => {
    if (!isEnabled && isAnimating) {
      stopAnimation();
    }
  }, [isEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const animateStep = useCallback((stepIndex: number) => {
    const sequence = sequenceRef.current;

    if (stepIndex >= sequence.length) {
      // Animation complete, loop back to start
      // console.log('Animation sequence complete, looping...');
      currentStepRef.current = 0;
      animateStep(0);
      return;
    }

    const step = sequence[stepIndex];
    // console.log(`Animating step ${stepIndex}:`, step);

    // Update current animated note
    setCurrentAnimatedNote({
      stringIndex: step.stringIndex,
      fret: step.fret,
      note: step.note,
      interval: step.interval
    });

    // Schedule next step
    const delay = getStepDelay(step);
    animationTimeoutRef.current = setTimeout(() => {
      currentStepRef.current = stepIndex + 1;
      animateStep(stepIndex + 1);
    }, delay);
  }, []);

  const startAnimation = useCallback(() => {
    if (sequenceRef.current.length === 0) {
      console.warn('No animation sequence available');
      return;
    }

    //console.log('Starting scale animation with', sequenceRef.current.length, 'steps');
    setIsAnimating(true);
    currentStepRef.current = 0;
    animateStep(0);
  }, [animateStep]);

  const stopAnimation = useCallback(() => {
    setIsAnimating(false);
    setCurrentAnimatedNote(null);
    currentStepRef.current = 0;

    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
  }, []);

  const toggleAnimation = useCallback(() => {
    if (isAnimating) {
      stopAnimation();
    } else {
      startAnimation();
    }
  }, [isAnimating, startAnimation, stopAnimation]);

  return {
    isAnimating,
    currentAnimatedNote,
    toggleAnimation,
    startAnimation,
    stopAnimation
  };
}
